import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@shared/components/ui/input-otp';
import { useAuthStore } from '@features/auth/hooks/useAuthStore';
import { clearCurrentPartner } from '@shared/utils/partner.utils';

interface OtpChallengeFormProps {
  /** Called when the user backs out of the challenge (returns to sign in). */
  onCancel: () => void;
}

const OTP_LENGTH = 6;

/**
 * Post-password TOTP challenge. Shown when an account has an authenticator app
 * enrolled. On success the auth store finalizes the session and we navigate in.
 */
export function OtpChallengeForm({ onCancel }: OtpChallengeFormProps) {
  const navigate = useNavigate();
  const { verifyOtp, cancelOtp, pendingOtp, error, clearError } = useAuthStore();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const email = pendingOtp?.email;

  const submit = async (value: string) => {
    if (value.length !== OTP_LENGTH || submitting) return;
    setSubmitting(true);
    clearError();
    try {
      await verifyOtp(value);
      clearCurrentPartner();
      toast.success('Signed in successfully');
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Verification failed');
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    cancelOtp();
    onCancel();
  };

  return (
    <div className="flex flex-col">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-muted">
          <ShieldCheck className="size-6 text-primary" />
        </div>
        <h2 className="mb-2 text-2xl font-semibold text-foreground">Two-step verification</h2>
        <p className="text-pretty text-sm text-muted-foreground">
          Enter the 6-digit code from your authenticator app
          {email ? (
            <>
              {' '}
              for <span className="font-medium text-foreground">{email}</span>
            </>
          ) : null}
          .
        </p>
      </div>

      {error && (
        <Alert variant="destructive" icon={AlertCircle} className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit(code);
        }}
        className="flex flex-col items-center gap-6"
      >
        <InputOTP
          maxLength={OTP_LENGTH}
          value={code}
          onChange={(value) => {
            setCode(value);
            if (value.length === OTP_LENGTH) void submit(value);
          }}
          disabled={submitting}
          autoFocus
          aria-label="Authenticator code"
        >
          <InputOTPGroup>
            {Array.from({ length: OTP_LENGTH }).map((_, i) => (
              <InputOTPSlot key={i} index={i} />
            ))}
          </InputOTPGroup>
        </InputOTP>

        <Button
          type="submit"
          className="h-11 w-full font-medium"
          disabled={submitting || code.length !== OTP_LENGTH}
        >
          {submitting ? 'Verifying...' : 'Verify and sign in'}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={handleCancel}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80"
        >
          <ArrowLeft className="size-4" />
          Back to sign in
        </button>
      </div>
    </div>
  );
}
